import React from 'react';
import { useParams, Link } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Calendar, ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';

type Article = {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  content: string;
  imageUrl?: string | null;
  author?: string | null;
  publishedAt?: string | null;
};

export default function BlogView() {
  const { t, i18n } = useTranslation();
  const params = useParams<{ slug: string }>();
  const slug = params?.slug;

  const { data, isLoading, error } = useQuery<Article>({
    queryKey: ['/api/articles', slug],
    enabled: !!slug,
    queryFn: async () => {
      const res = await fetch(`/api/articles/${slug}`);
      if (!res.ok) throw new Error('Not found');
      return res.json();
    },
  });

  const formatDate = (date?: string | null) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleDateString(i18n.language === 'en' ? 'en-US' : 'es-ES', {
      year: 'numeric', month: 'long', day: 'numeric'
    });
  };

  if (isLoading) {
    return <div className="container mx-auto px-4 py-8"><div className="h-64 rounded-lg bg-gray-100 animate-pulse" /></div>;
  }

  if (error || !data) {
    return <div className="container mx-auto px-4 py-8 text-red-600">{t('common.notFound', { defaultValue: 'No encontrado' })}</div>;
  }

  const isLikelyHtml = /<\/?[a-z][\s\S]*>/i.test(data.content || '');

  return (
    <article className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <Link href="/blog" className="text-primary hover:text-primary/80 inline-flex items-center gap-1">
          <ArrowLeft className="h-4 w-4" /> {t('common.back', { defaultValue: 'Volver' })}
        </Link>
      </div>
      <h1 className="text-3xl font-bold mb-3">{data.title}</h1>
      <div className="text-sm text-muted-foreground mb-6">
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4" />
          <span>{formatDate(data.publishedAt)}</span>
        </div>
        {data.author && (
          <div className="mt-1 italic">Autor: {data.author}</div>
        )}
      </div>
      {data.imageUrl && (
        <img src={data.imageUrl} alt={data.title} className="w-full rounded-lg mb-6" />
      )}
      <div className="prose max-w-none">
        {isLikelyHtml ? (
          <div dangerouslySetInnerHTML={{ __html: data.content }} />
        ) : (
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{data.content}</ReactMarkdown>
        )}
      </div>
    </article>
  );
}


